import apiClient from '../../config/axiosConfig';
import { API_BASE_URL } from '../../utils/apiUtils';

// ============================================================================
// VALIDATION FUNCTIONS
// ============================================================================

const validateTeamId = (teamId) => {
    if (!teamId || typeof teamId !== 'string' || teamId.trim() === '') {
        throw new Error('Team ID is required and must be a valid string');
    }
    return teamId.trim();
};

const validateTemplateId = (templateId) => {
    if (!templateId || typeof templateId !== 'string' || templateId.trim() === '') {
        throw new Error('Template ID is required and must be a valid string');
    }
    return templateId.trim();
};

const validateSignatureName = (name) => {
    if (!name || typeof name !== 'string' || name.trim() === '') {
        throw new Error('Signature name is required');
    }
    if (name.trim().length > 100) {
        throw new Error('Signature name cannot exceed 100 characters');
    }
    return name.trim();
};

// ============================================================================
// ERROR HANDLER
// ============================================================================

const handleEmailSignatureApiError = (error, operation = 'Email Signature API operation') => {
    console.error(`[EmailSignatureAPI] ${operation} Error:`, {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        requestUrl: error.config?.url,
        requestMethod: error.config?.method
    });

    if (error.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
        throw new Error('Your session has expired. Please log in again');
    }

    if (error.response?.status === 403) {
        throw new Error('You do not have permission to perform this action');
    }

    if (error.response?.status === 404) {
        throw new Error('Email signature template not found');
    }

    if (error.response?.status === 409) {
        throw new Error('A signature with this name already exists for this team');
    }

    if (error.response?.status >= 500) {
        throw new Error('Server error. Please try again later.');
    }

    if (error.code === 'ERR_NETWORK') {
        throw new Error('Network error. Please check your connection and try again.');
    }

    if (error.code === 'ECONNABORTED') {
        throw new Error('Request timeout. Please try again.');
    }

    const errorMessage = error.response?.data?.message || error.message || 'An unexpected error occurred';
    throw new Error(errorMessage);
};

// ============================================================================
// EMAIL SIGNATURE CRUD OPERATIONS
// ============================================================================

/**
 * Create a new email signature template
 * POST /api/team/:teamId/email-signatures
 * @param {string} teamId - Team ID
 * @param {Object} templateData - Template data
 * @returns {Promise<Object>} Created template data
 */
export const createEmailSignature = async (teamId, templateData) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validSignatureName = validateSignatureName(templateData.signatureName);

        console.log('[EmailSignatureAPI] Creating email signature template:', validSignatureName);

        const response = await apiClient.post(
            `${API_BASE_URL}/api/team/${validTeamId}/email-signatures`,
            {
                signatureName: validSignatureName,
                emailSignatureShowName: templateData.emailSignatureShowName !== undefined ? templateData.emailSignatureShowName : true,
                emailSignatureShowPronouns: templateData.emailSignatureShowPronouns !== undefined ? templateData.emailSignatureShowPronouns : false,
                emailSignatureShowJobTitle: templateData.emailSignatureShowJobTitle !== undefined ? templateData.emailSignatureShowJobTitle : true,
                emailSignatureShowCompany: templateData.emailSignatureShowCompany !== undefined ? templateData.emailSignatureShowCompany : true,
                emailSignatureShowLocation: templateData.emailSignatureShowLocation !== undefined ? templateData.emailSignatureShowLocation : true,
                emailSignatureShowPhoneNumber: templateData.emailSignatureShowPhoneNumber !== undefined ? templateData.emailSignatureShowPhoneNumber : true,
                emailSignatureShowProfilePic: templateData.emailSignatureShowProfilePic !== undefined ? templateData.emailSignatureShowProfilePic : true,
                emailSignatureShowCompanyLogo: templateData.emailSignatureShowCompanyLogo !== undefined ? templateData.emailSignatureShowCompanyLogo : true,
                emailSignatureIncludeQRCode: templateData.emailSignatureIncludeQRCode !== undefined ? templateData.emailSignatureIncludeQRCode : true,
                emailSignatureIncludeBannerImage: templateData.emailSignatureIncludeBannerImage !== undefined ? templateData.emailSignatureIncludeBannerImage : false,
                emailSignatureLinkTypes: templateData.emailSignatureLinkTypes || [],
                emailSignatureDisclaimer: templateData.emailSignatureDisclaimer || '',
                emailSignatureBannerImageUrl: templateData.emailSignatureBannerImageUrl || null,
                // Content fields
                emailSignatureName: templateData.emailSignatureName || '',
                emailSignatureJobTitle: templateData.emailSignatureJobTitle || '',
                emailSignatureCompany: templateData.emailSignatureCompany || '',
                emailSignatureLocation: templateData.emailSignatureLocation || '',
                emailSignaturePhoneNumber: templateData.emailSignaturePhoneNumber || '',
                emailSignatureProfilePictureUrl: templateData.emailSignatureProfilePictureUrl || '',
                emailSignatureCompanyLogoUrl: templateData.emailSignatureCompanyLogoUrl || '',
                isDefault: templateData.isDefault || false
            }
        );

        console.log('[EmailSignatureAPI] Email signature template created successfully');
        return response.data;
    } catch (error) {
        console.error('[EmailSignatureAPI] Error creating email signature template:', error);
        handleEmailSignatureApiError(error, 'Creating email signature template');
    }
};

/**
 * Get all email signature templates for a team
 * GET /api/team/:teamId/email-signatures
 * @param {string} teamId - Team ID
 * @param {Object} options - Query options (page, limit)
 * @returns {Promise<Object>} Templates list with pagination
 */
export const getEmailSignatures = async (teamId, options = {}) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const { page = 1, limit = 50 } = options;

        console.log('[EmailSignatureAPI] Fetching email signature templates for team:', validTeamId);

        const response = await apiClient.get(
            `${API_BASE_URL}/api/team/${validTeamId}/email-signatures`,
            {
                params: { page, limit }
            }
        );

        console.log('[EmailSignatureAPI] Email signature templates fetched successfully');
        return response.data;
    } catch (error) {
        console.error('[EmailSignatureAPI] Error fetching email signature templates:', error);
        handleEmailSignatureApiError(error, 'Fetching email signature templates');
    }
};

/**
 * Get a single email signature template
 * GET /api/team/:teamId/email-signatures/:templateId
 * @param {string} teamId - Team ID
 * @param {string} templateId - Template ID
 * @returns {Promise<Object>} Template data
 */
export const getEmailSignature = async (teamId, templateId) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validTemplateId = validateTemplateId(templateId);

        console.log('[EmailSignatureAPI] Fetching email signature template:', validTemplateId);

        const response = await apiClient.get(
            `${API_BASE_URL}/api/team/${validTeamId}/email-signatures/${validTemplateId}`
        );

        console.log('[EmailSignatureAPI] Email signature template fetched successfully');
        return response.data;
    } catch (error) {
        console.error('[EmailSignatureAPI] Error fetching email signature template:', error);
        handleEmailSignatureApiError(error, 'Fetching email signature template');
    }
};

/**
 * Update an email signature template
 * PUT /api/team/:teamId/email-signatures/:templateId
 * @param {string} teamId - Team ID
 * @param {string} templateId - Template ID
 * @param {Object} updateData - Update data
 * @returns {Promise<Object>} Updated template data
 */
export const updateEmailSignature = async (teamId, templateId, updateData) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validTemplateId = validateTemplateId(templateId);

        const payload = {};

        if (updateData.signatureName !== undefined) {
            payload.signatureName = validateSignatureName(updateData.signatureName);
        }
        if (updateData.emailSignatureShowName !== undefined) payload.emailSignatureShowName = updateData.emailSignatureShowName;
        if (updateData.emailSignatureShowPronouns !== undefined) payload.emailSignatureShowPronouns = updateData.emailSignatureShowPronouns;
        if (updateData.emailSignatureShowJobTitle !== undefined) payload.emailSignatureShowJobTitle = updateData.emailSignatureShowJobTitle;
        if (updateData.emailSignatureShowCompany !== undefined) payload.emailSignatureShowCompany = updateData.emailSignatureShowCompany;
        if (updateData.emailSignatureShowLocation !== undefined) payload.emailSignatureShowLocation = updateData.emailSignatureShowLocation;
        if (updateData.emailSignatureShowPhoneNumber !== undefined) payload.emailSignatureShowPhoneNumber = updateData.emailSignatureShowPhoneNumber;
        if (updateData.emailSignatureShowProfilePic !== undefined) payload.emailSignatureShowProfilePic = updateData.emailSignatureShowProfilePic;
        if (updateData.emailSignatureShowCompanyLogo !== undefined) payload.emailSignatureShowCompanyLogo = updateData.emailSignatureShowCompanyLogo;
        if (updateData.emailSignatureIncludeQRCode !== undefined) payload.emailSignatureIncludeQRCode = updateData.emailSignatureIncludeQRCode;
        if (updateData.emailSignatureIncludeBannerImage !== undefined) payload.emailSignatureIncludeBannerImage = updateData.emailSignatureIncludeBannerImage;
        if (updateData.emailSignatureLinkTypes !== undefined) payload.emailSignatureLinkTypes = updateData.emailSignatureLinkTypes;
        if (updateData.emailSignatureDisclaimer !== undefined) payload.emailSignatureDisclaimer = updateData.emailSignatureDisclaimer;
        if (updateData.emailSignatureBannerImageUrl !== undefined) payload.emailSignatureBannerImageUrl = updateData.emailSignatureBannerImageUrl;
        // Content fields
        if (updateData.emailSignatureName !== undefined) payload.emailSignatureName = updateData.emailSignatureName;
        if (updateData.emailSignatureJobTitle !== undefined) payload.emailSignatureJobTitle = updateData.emailSignatureJobTitle;
        if (updateData.emailSignatureCompany !== undefined) payload.emailSignatureCompany = updateData.emailSignatureCompany;
        if (updateData.emailSignatureLocation !== undefined) payload.emailSignatureLocation = updateData.emailSignatureLocation;
        if (updateData.emailSignaturePhoneNumber !== undefined) payload.emailSignaturePhoneNumber = updateData.emailSignaturePhoneNumber;
        if (updateData.emailSignatureProfilePictureUrl !== undefined) payload.emailSignatureProfilePictureUrl = updateData.emailSignatureProfilePictureUrl;
        if (updateData.emailSignatureCompanyLogoUrl !== undefined) payload.emailSignatureCompanyLogoUrl = updateData.emailSignatureCompanyLogoUrl;
        if (updateData.isDefault !== undefined) payload.isDefault = updateData.isDefault;

        console.log('[EmailSignatureAPI] Updating email signature template:', validTemplateId);

        const response = await apiClient.put(
            `${API_BASE_URL}/api/team/${validTeamId}/email-signatures/${validTemplateId}`,
            payload
        );

        console.log('[EmailSignatureAPI] Email signature template updated successfully');
        return response.data;
    } catch (error) {
        console.error('[EmailSignatureAPI] Error updating email signature template:', error);
        handleEmailSignatureApiError(error, 'Updating email signature template');
    }
};

/**
 * Delete an email signature template
 * DELETE /api/team/:teamId/email-signatures/:templateId
 * @param {string} teamId - Team ID
 * @param {string} templateId - Template ID
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteEmailSignature = async (teamId, templateId) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validTemplateId = validateTemplateId(templateId);

        console.log('[EmailSignatureAPI] Deleting email signature template:', validTemplateId);

        const response = await apiClient.delete(
            `${API_BASE_URL}/api/team/${validTeamId}/email-signatures/${validTemplateId}`
        );

        console.log('[EmailSignatureAPI] Email signature template deleted successfully');
        return response.data;
    } catch (error) {
        console.error('[EmailSignatureAPI] Error deleting email signature template:', error);
        handleEmailSignatureApiError(error, 'Deleting email signature template');
    }
};

/**
 * Assign/unassign members to an email signature template
 * PATCH /api/team/:teamId/email-signatures/:templateId/assign-members
 * @param {string} teamId - Team ID
 * @param {string} templateId - Template ID
 * @param {Object} assignmentData - { assignMemberIds: [], unassignMemberIds: [], setAsDefault: boolean }
 * @returns {Promise<Object>} Assignment result
 */
export const assignMembersToEmailSignature = async (teamId, templateId, assignmentData) => {
    try {
        const validTeamId = validateTeamId(teamId);
        const validTemplateId = validateTemplateId(templateId);

        const { assignMemberIds = [], unassignMemberIds = [], setAsDefault = false } = assignmentData;

        if (!Array.isArray(assignMemberIds) || !Array.isArray(unassignMemberIds)) {
            throw new Error('assignMemberIds and unassignMemberIds must be arrays');
        }

        console.log('[EmailSignatureAPI] Assigning members to email signature template:', {
            templateId: validTemplateId,
            assignCount: assignMemberIds.length,
            unassignCount: unassignMemberIds.length,
            setAsDefault
        });

        const response = await apiClient.patch(
            `${API_BASE_URL}/api/team/${validTeamId}/email-signatures/${validTemplateId}/assign-members`,
            {
                assignMemberIds,
                unassignMemberIds,
                setAsDefault
            }
        );

        console.log('[EmailSignatureAPI] Members assigned successfully');
        return response.data;
    } catch (error) {
        console.error('[EmailSignatureAPI] Error assigning members:', error);
        handleEmailSignatureApiError(error, 'Assigning members to email signature template');
    }
};

